import { PublicFieldView } from "client/openapi";
import { Select } from "components/Select";

export type FieldValue = boolean | number | string | Array<number> | null;

/**
 * The input for one field's value, by the field's kind: subjects, a list
 * choice, yes or no, a date, a number or text. Used where a CRM value is
 * set directly (a board card's move, a form's values set on submit).
 */
export default function FieldValueInput({
  field,
  value,
  onChange,
  disabled = false,
}: {
  field: PublicFieldView;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
  disabled?: boolean;
}) {
  const options = (field.options || []).map((o) => ({
    value: String(o.id),
    label: o.name,
  }));

  switch (field.input) {
    case "subjects":
      return (
        <Select
          id={field.field}
          options={options}
          value={options.filter((o) =>
            ((value as number[]) || []).includes(Number(o.value))
          )}
          onChange={(selected) =>
            onChange((selected || []).map((o) => Number(o.value)))
          }
          isMulti
          placeholder="No subjects"
          isDisabled={disabled}
        />
      );
    case "choice":
      return (
        <Select
          id={field.field}
          options={options}
          value={options.find((o) => Number(o.value) === value) || null}
          onChange={(selected) =>
            onChange(selected ? Number(selected.value) : null)
          }
          isClearable
          placeholder="--Select--"
          isDisabled={disabled}
        />
      );
    case "boolean": {
      const yesNo = [
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ];
      return (
        <Select
          id={field.field}
          options={yesNo}
          value={value === true ? yesNo[0] : value === false ? yesNo[1] : null}
          onChange={(selected) =>
            onChange(selected ? selected.value === "true" : null)
          }
          isClearable
          placeholder="--Select--"
          isDisabled={disabled}
        />
      );
    }
    case "date":
      return (
        <input
          id={field.field}
          type="date"
          className="input"
          value={typeof value === "string" ? value : ""}
          min={
            typeof field.min_value === "string" ? field.min_value : undefined
          }
          max={
            typeof field.max_value === "string" ? field.max_value : undefined
          }
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
        />
      );
    case "number":
      return (
        <input
          id={field.field}
          type="number"
          step="any"
          className="input"
          value={typeof value === "number" ? value : ""}
          min={
            typeof field.min_value === "number" ? field.min_value : undefined
          }
          max={
            typeof field.max_value === "number" ? field.max_value : undefined
          }
          onChange={(e) =>
            onChange(e.target.value === "" ? null : Number(e.target.value))
          }
          disabled={disabled}
        />
      );
    default:
      return (
        <input
          id={field.field}
          type="text"
          className="input"
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
        />
      );
  }
}
